import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PortalBootOverlay } from "@/components/PortalBootOverlay";
import { PORTAL_BOOT_PENDING_KEY } from "@/lib/portalBootSession";
import { resolvePostAuthPath, saveLastPath } from "@/lib/navigationState";
import { portalBasePath } from "@/lib/portalPaths";
import LoginPage from "@/pages/LoginPage";
import VerifyPage from "@/pages/VerifyPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import CRMPage from "@/pages/CRMPage";
import AdminProfilePage from "@/pages/AdminProfilePage";
import { AdminLayout } from "@/components/AdminLayout";
import RevenuePage from "@/pages/admin/RevenuePage";
import AdminHomePage from "@/pages/admin/AdminHomePage";
import OutreachPage from "@/pages/admin/OutreachPage";
import ProspectRecordPage from "@/pages/admin/ProspectRecordPage";
import ScopeReviewPage from "@/pages/ScopeReviewPage";
import BarterAgreementPage from "@/pages/BarterAgreementPage";
import PrivacyNoticePage from "@/pages/PrivacyNoticePage";
import { PortalLayout } from "@/components/PortalLayout";
import { ClientPortalGate } from "@/components/ClientPortalGate";
import { PortalLegacyRedirect } from "@/components/PortalLegacyRedirect";
import PortalIndexRoute from "@/pages/portal/PortalIndexRoute";
import ProjectTracker from "@/pages/portal/ProjectTracker";
import FileViewPage from "@/pages/portal/FileViewPage";
import ScopePacketPage, { ScopePacketLegacyRedirect } from "@/pages/portal/ScopePacketPage";
import FilesPage from "@/pages/portal/FilesPage";
import ApprovalsPage from "@/pages/portal/ApprovalsPage";
import CalendarRoute from "@/pages/portal/CalendarRoute";
import MessagesPage from "@/pages/portal/MessagesPage";
import ClientBoardPage from "@/pages/portal/ClientBoardPage";
import ActivityPage from "@/pages/portal/ActivityPage";
import AdminWorkspaceRedirect from "@/pages/portal/AdminWorkspaceRedirect";
import ClientHomeOnly from "@/pages/portal/ClientHomeOnly";

const Protected = ({ children, role }) => {
  const { user, client, loading } = useAuth();
  if (loading) return <div className="p-12 t-meta">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (role && user.role !== role) {
    const fallback = user.role === "admin" ? "/admin" : (client ? portalBasePath(client) : "/");
    return <Navigate to={fallback} replace />;
  }
  return children;
};

const RoleRedirect = () => {
  const { user, client, loading } = useAuth();
  if (loading) return <div className="p-12 t-meta">Loading…</div>;
  if (!user) return <LoginPage />;
  return <Navigate to={resolvePostAuthPath(user, client)} replace />;
};

const LastPathTracker = () => {
  const location = useLocation();
  useEffect(() => {
    saveLastPath(`${location.pathname}${location.search || ""}`);
  }, [location.pathname, location.search]);
  return null;
};

/** Full-screen post-login loader for both `/admin` and client portal (PortalLayout never mounts for admin-first landing). */
const PostLoginBootShell = ({ children }) => {
  const { user, portalBootPending, acknowledgePortalBoot } = useAuth();
  let storagePending = false;
  try {
    if (typeof window !== "undefined") {
      storagePending = sessionStorage.getItem(PORTAL_BOOT_PENDING_KEY) === "1";
    }
  } catch {
    storagePending = false;
  }
  const showBoot = Boolean(user) && (portalBootPending || storagePending);
  return (
    <>
      {children}
      {showBoot ? <PortalBootOverlay onFinished={acknowledgePortalBoot} /> : null}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            offset={20}
            gap={10}
            toastOptions={{
              className: "t-toast",
              classNames: {
                toast: "t-toast",
                title: "t-toast__title",
                description: "t-toast__desc",
                success: "t-toast--success",
                error: "t-toast--error",
              },
            }}
          />
          <PostLoginBootShell>
            <LastPathTracker />
            <Routes>
              <Route path="/" element={<RoleRedirect />} />
              <Route path="/:clientSlug/proposal" element={<ScopeReviewPage />} />
              <Route path="/scope/:token" element={<ScopeReviewPage />} />
              <Route path="/agreement/preview/:partnerSlug" element={<BarterAgreementPage />} />
              <Route path="/agreement/:token" element={<BarterAgreementPage />} />
              <Route path="/privacy" element={<PrivacyNoticePage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
                <Route index element={<AdminHomePage />} />
                <Route path="outreach" element={<OutreachPage />} />
                <Route path="outreach/:clientId" element={<ProspectRecordPage />} />
                <Route path="engagements" element={<AdminDashboard />} />
                <Route path="crm" element={<CRMPage />} />
                <Route path="profile" element={<AdminProfilePage />} />
                <Route path="blu" element={<Navigate to="/admin" replace />} />
                <Route path="revenue" element={<RevenuePage />} />
              </Route>
              <Route path="/portal/*" element={<Protected><PortalLegacyRedirect /></Protected>} />
              <Route path="/:clientSlug/files/view/:fileId/*" element={<Protected><ClientPortalGate><FileViewPage /></ClientPortalGate></Protected>} />
              <Route path="/:clientSlug/files/scope" element={<ScopePacketPage />} />
              <Route path="/:clientSlug/files/packet/:fileId" element={<ScopePacketLegacyRedirect />} />
              <Route path="/:clientSlug" element={<Protected><ClientPortalGate><PortalLayout /></ClientPortalGate></Protected>}>
                <Route index element={<PortalIndexRoute />} />
                <Route path="tracker" element={<ClientHomeOnly><ProjectTracker /></ClientHomeOnly>} />
                <Route path="files" element={<ClientHomeOnly><FilesPage /></ClientHomeOnly>} />
                <Route path="approvals" element={<ClientHomeOnly><AdminWorkspaceRedirect><ApprovalsPage /></AdminWorkspaceRedirect></ClientHomeOnly>} />
                <Route path="tasks" element={<Navigate to="../tracker" replace />} />
                <Route path="calendar" element={<ClientHomeOnly><CalendarRoute /></ClientHomeOnly>} />
                <Route path="messages" element={<ClientHomeOnly><AdminWorkspaceRedirect><MessagesPage /></AdminWorkspaceRedirect></ClientHomeOnly>} />
                <Route path="notes" element={<ClientHomeOnly><AdminWorkspaceRedirect><ClientBoardPage /></AdminWorkspaceRedirect></ClientHomeOnly>} />
                <Route path="resources" element={<Navigate to="../tracker" replace />} />
                <Route path="activity" element={<ClientHomeOnly><AdminWorkspaceRedirect><ActivityPage /></AdminWorkspaceRedirect></ClientHomeOnly>} />
              </Route>
            </Routes>
          </PostLoginBootShell>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
